import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info } from './Icons';

interface InfoTooltipButtonProps {
  label: string;
  content?: ReactNode;
  iconClassName?: string;
  variant?: 'button' | 'plain';
  icon?: 'info' | 'warning';
  compact?: boolean;
}

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function InfoTooltipButton({
  label,
  content,
  iconClassName = 'h-4 w-4',
  variant = 'button',
  icon = 'info',
  compact = false,
}: InfoTooltipButtonProps) {
  const tooltipId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const pointerInteractionRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({
    left: 0,
    top: 0,
    width: 320,
    maxHeight: undefined as number | undefined,
  });

  useBrowserLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    let remeasureFrame: number | null = null;

    const applyPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const viewportPadding = 8;
      const visualViewport = window.visualViewport;
      const viewportLeft = visualViewport?.offsetLeft ?? 0;
      const viewportTop = visualViewport?.offsetTop ?? 0;
      const viewportWidth = visualViewport?.width ?? window.innerWidth;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const tooltipWidth = Math.max(
        0,
        Math.min(320, viewportWidth - viewportPadding * 2),
      );
      const maxTooltipHeight = Math.max(0, viewportHeight - viewportPadding * 2);
      const tooltipHeight = Math.min(
        tooltipRef.current?.offsetHeight ?? 0,
        maxTooltipHeight,
      );
      setPosition({
        left: Math.min(
          Math.max(viewportLeft + viewportPadding, rect.right - tooltipWidth),
          viewportLeft + viewportWidth - tooltipWidth - viewportPadding,
        ),
        top: Math.min(
          Math.max(
            viewportTop + viewportPadding,
            rect.top - tooltipHeight - viewportPadding,
          ),
          viewportTop + viewportHeight - tooltipHeight - viewportPadding,
        ),
        width: tooltipWidth,
        maxHeight: maxTooltipHeight,
      });
    };

    const updatePosition = () => {
      applyPosition();
      if (remeasureFrame !== null) {
        window.cancelAnimationFrame(remeasureFrame);
      }
      remeasureFrame = window.requestAnimationFrame(() => {
        remeasureFrame = null;
        applyPosition();
      });
    };

    const handleScroll = () => {
      const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
      if (viewportWidth < 640) {
        setOpen(false);
        return;
      }
      updatePosition();
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', handleScroll, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', handleScroll);
    return () => {
      if (remeasureFrame !== null) {
        window.cancelAnimationFrame(remeasureFrame);
      }
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', handleScroll, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  const buttonSizeClassName = compact ? 'h-5 w-5' : 'h-8 w-8';
  const buttonClassName = variant === 'plain'
    ? `inline-flex ${buttonSizeClassName} items-center justify-center text-slate-300 transition-colors hover:text-orange-200 focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-orange-300/70`
    : `inline-flex ${buttonSizeClassName} items-center justify-center rounded-full bg-slate-950/45 text-slate-100 shadow-lg shadow-white/20 transition-colors hover:bg-slate-700/80 hover:text-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70`;
  const TooltipIcon = icon === 'warning' ? AlertTriangle : Info;

  return (
    <span className="inline-flex">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        onPointerDown={() => {
          pointerInteractionRef.current = true;
        }}
        onPointerCancel={() => {
          pointerInteractionRef.current = false;
        }}
        onClick={(event) => {
          if (event.detail === 0) {
            setOpen(true);
          } else {
            setOpen((current) => !current);
          }
          pointerInteractionRef.current = false;
        }}
        onMouseEnter={() => {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) setOpen(true);
        }}
        onMouseLeave={() => {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) setOpen(false);
        }}
        onFocus={() => {
          if (!pointerInteractionRef.current) setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            buttonRef.current?.focus();
          }
        }}
        className={buttonClassName}
      >
        <TooltipIcon className={`${iconClassName} flex-shrink-0`} />
      </button>
      {open && createPortal(
        <span
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          style={{
            left: position.left,
            top: position.top,
            width: position.width,
            maxHeight: position.maxHeight,
          }}
          className="info-tooltip-popover fixed z-[9999] overflow-y-auto rounded-xl border px-4 py-3 text-left text-xs font-medium normal-case leading-5 shadow-2xl whitespace-pre-line"
        >
          {content ?? label}
        </span>,
        document.body,
      )}
    </span>
  );
}
